import { createTheme } from '@mui/material/styles';

// Enable CSS variables TypeScript support
import type {} from '@mui/material/themeCssVarsAugmentation';

// Augment the palette interfaces to include custom variables
declare module '@mui/material/styles' {
  interface Theme {
    textSizes: {
      xs: string;
      small: string;
      medium: string;
      large: string;
    };
  }

  interface ThemeOptions {
    textSizes?: {
      xs?: string;
      small?: string;
      medium?: string;
      large?: string;
    };
  }

  // Augment the existing TypeBackground instead of replacing background
  interface TypeBackground {
    offWhite: string;
  }

  interface PaletteOptions {
    brand?: {
      primary: string;
      secondary: string;
    };
    button?: {
      test: string;
    };
    gradient?: string;
    border?: {
      subtle: string;
      strong: string;
    };
    // Remove the background override - it's handled by TypeBackground augmentation above
    custom?: {
      surface: string;
      accent: string;
    };
  }

  interface Palette {
    brand: {
      primary: string;
      secondary: string;
    };
    button: {
      test: string;
    };
    gradient: string;
    border: {
      subtle: string;
      strong: string;
    };
    // Remove the background override - it's handled by TypeBackground augmentation above
    custom: {
      surface: string;
      accent: string;
    };
  }
}

const fontFamilyHeaders = "'Poppins', sans-serif";
const fontFamilyMain = "'Poppins', sans-serif";

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0E4FC2',
    },
    secondary: {
      main: '#6B7280',
    },
    grey: {
      700: '#364153',
    },
    brand: {
      primary: '#0E4FC2',
      secondary: '#dc004e',
    },
    button: {
      test: '#a0a0a0',
    },
    gradient: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    border: {
      subtle: '#e0e0e0',
      strong: '#bdbdbd',
    },
    custom: {
      surface: '#f5f5f5',
      accent: '#ff5722',
    },
    background: {
      offWhite: '#f3f4f6',
    },
  },
  cssVariables: {
    cssVarPrefix: 'app',
    colorSchemeSelector: 'data',
  },
  spacing: 8,
  defaultColorScheme: 'light', // Default to light mode
  textSizes: {
    xs: '0.75rem',
    small: '0.875rem',
    medium: '0.95rem',
    large: '1.125rem',
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    h1: {
      fontFamily: fontFamilyHeaders,
      fontWeight: 700,
      fontSize: '2.25rem',
      textTransform: 'uppercase',
      color: '#000000',
      margin: 0,
      '@media (max-width: 768px)': {
        fontSize: '1.5rem',
      },
    },
    h2: {
      fontFamily: fontFamilyHeaders,
      fontWeight: 700,
      fontSize: '1.875rem',
      textTransform: 'uppercase',
      color: '#111827',
      margin: '0 0 36px 0',
      textAlign: 'left',
      '@media (max-width: 768px)': {
        fontSize: '1.3125rem',
      },
    },
    h3: { fontFamily: fontFamilyHeaders, textTransform: 'uppercase' },
    h4: { fontFamily: fontFamilyHeaders, color: '#111827', fontWeight: 700, fontSize: '1.25rem', textTransform: 'uppercase' },
    h5: { fontFamily: fontFamilyHeaders, color: '#6B7280', fontWeight: 400, fontSize: '1rem', textTransform: 'uppercase' },
    h6: { fontFamily: fontFamilyHeaders, color: '#6B7280', fontSize: '0.875rem', fontWeight: 400, textTransform: 'uppercase' },
    subtitle2: {
      fontFamily: fontFamilyMain,
      fontWeight: 400,
      fontSize: '0.6875rem',
      lineHeight: 1.5,
      color: '#6b7280',
      margin: 0,
      textAlign: 'center',
    },
    body1: {
      fontFamily: fontFamilyMain,
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
      color: '#374151',
      display: 'block',
    },
    body2: {
      fontFamily: fontFamilyMain,
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
    },
    fontFamily: fontFamilyMain,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '.pointer': {
          cursor: 'pointer',
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&:hover': {
            scrollbarColor: '#0E4FC2 transparent',
          },
        },
        '*::-webkit-scrollbar': {
          width: '10px',
          height: '10px',
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
          border: '2px solid transparent',
          backgroundClip: 'content-box',
          transition: 'background-color 0.2s ease',
        },
        '*:hover::-webkit-scrollbar-thumb': {
          backgroundColor: '#0E4FC2',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          width: '16px',
          height: '16px',
          '&.Mui-checked': {
            color: '#0E4FC2',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginBottom: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 900px)': {
            maxWidth: 'none',
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
        },
        sizeMedium: {
          padding: 12,
          fontSize: '1rem',
          fontWeight: 600,
        },
      },
    },
  },
});

export default theme;
